// colDef/ticketColDef.tsx
import { TooltipText } from "@/components/ToolTip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  TicketPriority,
  TicketStatus,
  TicketType,
} from "@/services/ticket.service";
import { formatDate } from "@/utils/formatDate.util";
import { createColumnHelper } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const columnHelper = createColumnHelper<TicketType>();

export type TicketActionType = {
  id: string;
  name: string;
  action: "edit" | "delete";
};

// ─── Status pill config ───────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  TicketStatus,
  { label: string; className: string }
> = {
  open: {
    label: "Open",
    className: "bg-blue-100 text-blue-700",
  },
  in_progress: {
    label: "In Progress",
    className: "bg-amber-100 text-amber-700",
  },
  resolved: {
    label: "Resolved",
    className: "bg-green-100 text-green-700",
  },
  closed: {
    label: "Closed",
    className: "bg-gray-100 text-gray-500",
  },
};

// ─── Priority pill config ─────────────────────────────────────────────────────

const PRIORITY_CONFIG: Record<
  TicketPriority,
  { label: string; className: string }
> = {
  critical: {
    label: "Critical",
    className: "bg-red-100 text-red-700",
  },
  high: {
    label: "High",
    className: "bg-orange-100 text-orange-700",
  },
  medium: {
    label: "Medium",
    className: "bg-blue-100 text-blue-700",
  },
  low: {
    label: "Low",
    className: "bg-gray-100 text-gray-500",
  },
};

// ─── Columns ──────────────────────────────────────────────────────────────────

export const createTicketColumns = (
  onActionClick: (payload: TicketActionType) => void
) => [
  // Ticket number
  columnHelper.accessor("ticketNumber", {
    header: "Ticket Id",
    cell: (info) => (
      <span className="whitespace-nowrap text-xs font-medium text-gray-500">
        {info.getValue()}
      </span>
    ),
  }),

  // Title + description (two-line)
  columnHelper.accessor("title", {
    header: "Title",
    cell: (info) => {
      const { title, description, _id } = info.row.original;
      return (
        <div className="max-w-xs">
          <Link
            to={`/tickets/${_id}`}
            className="block font-semibold hover:underline"
          >
            <TooltipText text={title} maxChars={40} />
          </Link>
          <TooltipText
            text={description}
            truncate
            className="mt-0.5 max-w-[200px] text-xs text-gray-400"
          />
        </div>
      );
    },
  }),

  // Status pill
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const cfg = STATUS_CONFIG[info.getValue()];
      return (
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // Priority pill
  columnHelper.accessor("priority", {
    header: "Priority",
    cell: (info) => {
      const cfg = PRIORITY_CONFIG[info.getValue()];
      return (
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // Category
  columnHelper.accessor("category", {
    header: "Category",
    cell: (info) => (
      <span className="rounded-sm border border-gray-200 p-1 text-sm">
        {info.getValue()}
      </span>
    ),
  }),

  // Raised By — name (bold) + role (gray sub-text)
  columnHelper.accessor("raisedBy", {
    header: "Raised By",
    cell: (info) => {
      const { name, role } = info.getValue();
      return (
        <div>
          <p className="font-medium">{name}</p>
          <p className="text-xs text-gray-400">{role}</p>
        </div>
      );
    },
  }),

  // Assigned To — tooltip on long names
  columnHelper.accessor("assignedTo", {
    header: "Assigned To",
    cell: (info) => (
      <TooltipText
        text={info.getValue().name}
        maxChars={25}
        className="text-sm"
      />
    ),
  }),

  // Created date — DD/MM/YYYY
  columnHelper.accessor("createdAt", {
    header: "Created",
    cell: (info) => (
      <span className="whitespace-nowrap text-sm">
        {formatDate(info.getValue(), { locale: "en-GB", dateStyle: "short" })}
      </span>
    ),
  }),

  // Inline edit / delete
  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: (info) => {
      const data = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-500 hover:text-blue-600"
            onClick={() =>
              onActionClick({ id: data._id, name: data.title, action: "edit" })
            }
            aria-label="Edit ticket"
          >
            <Pencil size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-500 hover:text-red-600"
            onClick={() =>
              onActionClick({
                id: data._id,
                name: data.title,
                action: "delete",
              })
            }
            aria-label="Delete ticket"
          >
            <Trash2 size={15} />
          </Button>
        </div>
      );
    },
  }),
];
